import { useMemo } from 'react'

export interface PurchaseOrderListNavigation {
  canGoPrev: boolean
  canGoNext: boolean
  onPrev: () => void
  onNext: () => void
}

/**
 * Encapsulates prev/next navigation for the F0 purchase order sidepanel.
 *
 * Pure transformation of `(activeId, ids)` into the props the sidepanel title
 * needs to render chevron buttons. Returns `undefined` when the active id is
 * not in the list or the list has fewer than two items — so callers can skip
 * rendering without branching on multiple booleans.
 *
 * No wrap-around: at index 0 there is no prev, at the last index there is
 * no next.
 */
export const usePurchaseOrderListNavigation = (
  activeId: string,
  ids: readonly string[],
  onNavigate: (nextId: string) => void
): PurchaseOrderListNavigation | undefined => {
  return useMemo(() => {
    const currentIndex = ids.indexOf(activeId)
    const totalCount = ids.length
    if (currentIndex < 0 || totalCount < 2) return undefined

    const prevId = ids[currentIndex - 1]
    const nextId = ids[currentIndex + 1]
    const canGoPrev = prevId !== undefined
    const canGoNext = nextId !== undefined

    return {
      canGoPrev,
      canGoNext,
      onPrev: canGoPrev ? () => onNavigate(prevId) : () => {},
      onNext: canGoNext ? () => onNavigate(nextId) : () => {},
    }
  }, [ids, activeId, onNavigate])
}

/**
 * Replace the last path segment of a URL with `id`. Pure function that avoids
 * the "../${id}" drift bug that plagues relative-url resolution when called
 * repeatedly.
 */
export const replaceLastPathSegment = (url: URL, id: string): URL => {
  const next = new URL(url.toString())
  const segments = next.pathname.split('/')
  segments[segments.length - 1] = id
  next.pathname = segments.join('/')
  return next
}
